import { AgGridReact } from 'ag-grid-react';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from '../../utils/sidebar';
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import IconButton from '../../../../components/Buttons/IconButton';
import { IoCreateOutline } from 'react-icons/io5';
import { BsTrash3 } from 'react-icons/bs';
import DefaultPagination from '../../../../components/Pagination';
import ConfirmationModal from '../../../../components/Modals/Confirmation';
import { del, get } from '../../../../utils/helpers/api';
import { TfiFilter } from 'react-icons/tfi';
import { Badge } from 'reactstrap';
import { toast } from 'react-toastify';
import { printEmploymentStatus } from '../../utils/helpers';
import FiltersDrawer from './Drawers/filters';
import i18n from '../../../../i18n';

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate(`/hr/employees/edit/${data.id}`, { state: { employeeData: data } });
        }}
        size="sm" outline color="secondary"
      >
        <IoCreateOutline />
      </IconButton>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setEmployeeToDelete(data.id);
        }}
        size="sm" outline color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

function EmployeesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const FILTER_KEYS = ['search', 'phone_number', 'employment_status', 'added_after', 'added_before'];

  const [filters, setFilters] = useState(() => {
    const initial = {};
    FILTER_KEYS.forEach(key => {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    });
    return initial;
  });

  const filtersCount = Object.values(filters).filter(v => v).length;

  const getEmployees = useCallback(async () => {
    setLoading(true);
    let query = '/hr/employees?page=' + currentPage + '&page_size=' + pageSize;
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (Object.keys(activeFilters).length) {
      query += '&' + new URLSearchParams(activeFilters).toString();
    }
    const data = await get(query);
    if (data?.employees?.data) {
      setRowData(data.employees.data);
      setTotalPages(data.employees.last_page);
      setTotalRecords(data.employees.total);
    }
    setLoading(false);
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    getEmployees();
  }, [getEmployees]);

  const handleApplyFilters = (filtersData) => {
    const activeFilters = Object.fromEntries(Object.entries(filtersData).filter(([, v]) => v));
    setFilters(activeFilters);
    setSearchParams(activeFilters);
    setCurrentPage(1);
    setFiltersOpen(false);
  }

  const deleteEmployee = async () => {
    const data = await del('/hr/employees/delete/' + employeeToDelete);

    if (data.success) {
      toast.success(data.message || t('hr.employeeDeleted'));
      getEmployees();
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setEmployeeToDelete(null);
  };

  const columnDefs = useMemo(() => [
    {
      field: 'firstname',
      headerName: t('hr.employeeName'),
      minWidth: 200,
      flex: 1,
      sortable: true,
      pinned: i18n.language === 'ar' ? 'right' : 'left',
      valueFormatter: (params) => `${params.data.firstname} ${params.data.middlename ?? ''} ${params.data.lastname}`,
      onCellClicked: (params) => {
        navigate(`/hr/employees/view/${params.data.id}`, { state: { employeeData: params.data } });
      }
    },
    { 
      field: 'salary_amount', 
      headerName: t('hr.salary'), 
      width: 130, 
      sortable: true,
      valueFormatter: (params) => `${params.data.salary_amount ?? ''} ${params.data.salary_currency ?? ''}`
    },
    { field: 'email', headerName: t('common.email'), width: 180, sortable: true},
    { 
      field: 'phone_number', 
      headerName: t('common.phone'), 
      width: 140, 
      sortable: true,
      valueFormatter: (params) => `${params.data.country_code ?? ''} ${params.data.phone_number ?? ''}`
    },
    { field: 'contract_start_date', headerName: t('common.date'), width: 140, sortable: true},
    { field: 'department', headerName: t('hr.department'), width: 150, sortable: true},
    {
      field: 'employment_status',
      headerName: t('common.status'),
      width: 130,
      sortable: true,
      cellRenderer: (params) => printEmploymentStatus(params.value)
    },
    { 
      headerName: t('common.actions'), 
      minWidth: 100, 
      flex: 1, 
      sortable: false, 
      resizable: false, 
      cellRenderer: ActionsCellRenderer
    }
  ], [t]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridContext = useMemo(() => ({ navigate, setEmployeeToDelete }), [navigate]);

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('hr.employees')}>
          <button className="subheader-btn" type="button" onClick={() => setFiltersOpen(true)}>
            <TfiFilter size={13} />
            {t('common.filter')}
            {filtersCount > 0 && (
              <Badge color="primary" className="filter-badge">{filtersCount}</Badge>
            )}
          </button>
          <div className="subheader-divider" />
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/hr/employees/create')}>
            + {t('hr.newEmployee')}
          </DefaultButton>
        </PageSubHeader>

        <div className="subheader-page-content">
          <div className="ag-theme-alpine" style={{ flex: 1, minHeight: 0 }}>
            <AgGridReact
              enableRtl={i18n.language === 'ar'}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              animateRows={true}
              loading={loading}
              context={gridContext}
              suppressCellFocus={true}
            />
          </div>

          <DefaultPagination 
            currentPage={currentPage} 
            totalPages={totalPages} 
            onPageChange={setCurrentPage} 
          />
        </div>
      </div>

      <FiltersDrawer
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        filters={filters}
        applyFilters={handleApplyFilters}
      />

      <ConfirmationModal
        isOpen={employeeToDelete !== null}
        title={t('hr.deleteEmployee')}
        message={t('hr.deleteEmployeeConfirm')}
        onConfirm={deleteEmployee}
        toggle={() => setEmployeeToDelete(null)}
      />
    </SidebarLayout>
  );
}

export default EmployeesPage;
